import { useState, Fragment, useEffect } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Link, TextField, Snackbar, Alert, AlertColor, Tooltip, Typography, IconButton, InputAdornment } from '@mui/material';
import { deleteTestResultTable, updateResultTableRow } from 'services';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

const columns = [
    'Code',
    'Name',
    'Matrix Type',
    'Sample Number',
    'Price Per Sample',
    'Line Total',
    'Reference '
];

export const PriceTable = ({ result, id, status, gridReload, setSelectionTriggerMisc, saveAllTrigger, saveAllSilent }: any) => {
    const [editableData, setEditableData] = useState<any>({});
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
    const [notificationMessage, setNotificationMessage] = useState<{
        open: boolean;
        message: string;
        severity: AlertColor;
    }>({
        open: false,
        message: '',
        severity: 'info'
    });

    let list: any = [];
    let ids: any = [];

    if (result?.length > 0) {
        result?.forEach((item: any) => {
            if (item.quoteResultTableID === id) {
                list.push(item);
            }
        });
    }

    const handleRowDelete = (row: any, index: any) => {
        const allIds: any[] = [row.id];

        if (row.linkedTests !== null && row.linkedTests.length > 0) {
            row.linkedTests.forEach((i: any) => {
                allIds.push(i.id);
            })
        }

        ids = [...new Set(allIds)];
        deleteTestResultTable({ data: ids }).then((res: any) => {
            if (res.status === 200) {
                let index1 = result.findIndex((p: any) => p.id === row?.id);

                if (index1 !== -1) {
                    result?.splice(index1, 1);
                } else {
                    result.forEach((p: any) => {
                        if (p.linkedTests && Array.isArray(p.linkedTests)) {
                            const linkedTestIndex = p.linkedTests.findIndex((test: any) => test.id === row?.id);
                            if (linkedTestIndex !== -1) {
                                p.linkedTests.splice(linkedTestIndex, 1);
                            }
                        }
                    });
                }
                gridReload(true);
                setNotificationMessage({
                    open: true,
                    message: 'Row deleted successfully.',
                    severity: 'success'
                });
                ids = [];
                return result;
            } else {
                setNotificationMessage({
                    open: true,
                    message: 'Failed to delete the row.',
                    severity: 'error'
                });
            }
        }).catch((err: any) => {
            setNotificationMessage({
                open: true,
                message: err?.response?.data || 'Failed to delete the row.',
                severity: 'error'
            });
        });
    }

    const handleFieldChange = (rowId: string, field: string, value: string) => {
        setEditableData((prev: any) => ({
            ...prev,
            [rowId]: {
                ...prev[rowId],
                [field]: value
            }
        }));
    };

    const getFieldValue = (rowId: string, field: string, defaultValue: any) => {
        return editableData[rowId]?.[field] !== undefined ? editableData[rowId][field] : defaultValue;
    };

    const calculateLineTotal = (rowId: string, row: any) => {
        const noOfSamples = parseFloat(getFieldValue(rowId, 'noOfSamples', row.noOfSamples)) || 0;
        const testPrice = parseFloat(getFieldValue(rowId, 'testPrice', row.testPrice)) || 0;
        return (noOfSamples * testPrice).toFixed(2);
    };

    const calculateTotalSubtotal = () => {
        return list.reduce((total: number, row: any) => {
            return total + parseFloat(calculateLineTotal(row.id, row));
        }, 0).toFixed(2);
    };

    const handleClick = (user: any, rowId: any) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(rowId)) {
                newSet.delete(rowId);
            } else {
                newSet.add(rowId);
            }
            return newSet;
        });
    };

    useEffect(() => {
        if (!saveAllTrigger) return;

        const currentList: any[] = [];
        if (result?.length > 0) {
            result.forEach((item: any) => {
                if (item.quoteResultTableID === id) {
                    currentList.push(item);
                }
            });
        }

        const modifiedRows = currentList
            .filter((row: any) => editableData[row.id] !== undefined)
            .map((row: any) => ({
                id: row.id,
                code: editableData[row.id]?.code !== undefined ? editableData[row.id].code : row.code,
                testName: editableData[row.id]?.testName !== undefined ? editableData[row.id].testName : row.testName,
                noOfSamples: editableData[row.id]?.noOfSamples !== undefined ? editableData[row.id].noOfSamples : row.noOfSamples,
                testPrice: editableData[row.id]?.testPrice !== undefined ? editableData[row.id].testPrice : row.testPrice,
                reference: editableData[row.id]?.reference !== undefined ? editableData[row.id].reference : (row.reference || '')
            }));

        if (modifiedRows.length === 0) {
            if (!saveAllSilent) {
                setNotificationMessage({ open: true, message: 'No changes to save.', severity: 'info' });
            }
            return;
        }

        updateResultTableRow(modifiedRows).then((res: any) => {
            if (res?.status === 200) {
                setSelectionTriggerMisc((prev: any) => prev + 1);
                setEditableData({});
                setNotificationMessage({ open: true, message: 'All rows saved successfully.', severity: 'success' });
            } else {
                setNotificationMessage({ open: true, message: 'Failed to save rows.', severity: 'error' });
            }
        }).catch(() => {
            setNotificationMessage({ open: true, message: 'An error occurred while saving rows.', severity: 'error' });
        });
    }, [saveAllTrigger]);

    const handleRowSubmit = (row: any, index: any) => {
        const updatedRow = {
            id: row?.id,
            code: getFieldValue(row.id, 'code', row.code),
            testName: getFieldValue(row.id, 'testName', row.testName),
            noOfSamples: getFieldValue(row.id, 'noOfSamples', row.noOfSamples),
            testPrice: getFieldValue(row.id, 'testPrice', row.testPrice),
            reference: getFieldValue(row.id, 'reference', row.reference || '')
        };

        updateResultTableRow([updatedRow]).then((res: any) => {
            if (res?.status === 200) {
                setSelectionTriggerMisc((prev: any) => prev + 1);
                setNotificationMessage({
                    open: true,
                    message: 'Row updated successfully.',
                    severity: 'success'
                });
            } else {
                setNotificationMessage({
                    open: true,
                    message: 'Failed to update the row.',
                    severity: 'error'
                });
            }
        }).catch((error: any) => {
            setNotificationMessage({
                open: true,
                message: 'An error occurred while updating the row.',
                severity: 'error'
            });
        })
    };


    return (
        <>
            <TableContainer component={Paper}>
                <Table
                    id={id}
                    sx={{
                        minWidth: 700,
                        border: '1px solid #ddd',
                        paddingTop: 10
                    }}
                    aria-label="spanning table">
                    <TableHead style={{ backgroundColor: '#003883' }}>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell style={{ color: '#fff', padding: '16px' }}>{column}</TableCell>
                            ))}
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {list && list.map((row: any, index: any) => (
                            <>
                                <TableRow key={row?.id}>
                                    <TableCell style={{ padding: '8px 16px' }}>
                                        {row?.billingGroupID > 0 ?
                                            <Typography variant="body1" sx={{ fontSize: '12px' }}>{row.code}</Typography>
                                            : <TextField
                                                size="small"
                                                value={String(getFieldValue(row.id, 'code', row.code) || '')}
                                                onChange={(e) => handleFieldChange(row.id, 'code', e.target.value)}
                                                variant="outlined"
                                                disabled={status === 'view'}
                                            />}
                                        {String(getFieldValue(row.id, 'code', row.code) || '') === 'Grouped' ? <b>*</b> : null}
                                    </TableCell>
                                    <TableCell style={{ padding: '8px 16px' }}>
                                        {row?.linkedTests?.length > 0 ? (
                                            row?.billingGroupID > 0 ? (
                                                <Tooltip
                                                    title={String(row.testName || '')}
                                                    arrow
                                                    placement="top"
                                                >
                                                    <Link
                                                        component="button"
                                                        variant="body2"
                                                        onClick={() => handleClick(row, row.id)}
                                                        title="Group"
                                                        sx={{
                                                            display: 'inline-block',
                                                            maxWidth: '100%',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {row.testName}
                                                    </Link>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip
                                                    title={String(getFieldValue(row.id, 'testName', row.testName) || '')}
                                                    arrow
                                                    placement="top"
                                                >
                                                    <span style={{ display: 'block', width: '100%' }}>
                                                        <TextField
                                                            size="small"
                                                            value={String(getFieldValue(row.id, 'testName', row.testName) || '')}
                                                            onChange={(e) =>
                                                                handleFieldChange(row.id, 'testName', e.target.value)
                                                            }
                                                            variant="outlined"
                                                            disabled={status === 'view'}
                                                            sx={{ width: '100%' }}
                                                            InputProps={{
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleClick(row, row.id)}
                                                                            title={
                                                                                expandedRows.has(row.id)
                                                                                    ? 'Collapse Group'
                                                                                    : 'Expand Group'
                                                                            }
                                                                            sx={{
                                                                                padding: '2px',
                                                                                backgroundColor: '#003883',
                                                                                color: '#fff',
                                                                                '&:hover': {
                                                                                    backgroundColor: '#002a62',
                                                                                },
                                                                            }}
                                                                        >
                                                                            {expandedRows.has(row.id) ? (
                                                                                <RemoveIcon fontSize="small" />
                                                                            ) : (
                                                                                <AddIcon fontSize="small" />
                                                                            )}
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                    </span>
                                                </Tooltip>
                                            )
                                        ) : (
                                            <Tooltip
                                                title={String(getFieldValue(row.id, 'testName', row.testName) || '')}
                                                arrow
                                                placement="top"
                                            >
                                                <span style={{ display: 'block', width: '100%' }}>
                                                    <TextField
                                                        size="small"
                                                        value={String(getFieldValue(row.id, 'testName', row.testName) || '')}
                                                        onChange={(e) =>
                                                            handleFieldChange(row.id, 'testName', e.target.value)
                                                        }
                                                        variant="outlined"
                                                        disabled={status === 'view'}
                                                        sx={{ width: '100%' }}
                                                    />
                                                </span>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell style={{ padding: '8px 16px' }}>{String(row?.userMatrixType || '')}</TableCell>
                                    <TableCell style={{ padding: '8px 16px' }}>
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={String(getFieldValue(row.id, 'noOfSamples', row.noOfSamples) ?? '')}
                                            onChange={(e) => handleFieldChange(row.id, 'noOfSamples', e.target.value)}
                                            variant="outlined"
                                            disabled={status === 'view'}
                                        />
                                    </TableCell>
                                    <TableCell style={{ padding: '8px 16px' }}>
                                        <TextField
                                            size="small"
                                            type="number"
                                            inputProps={{ step: "0.01" }}
                                            value={String(getFieldValue(row.id, 'testPrice', row.testPrice) ?? '')}
                                            onChange={(e) => handleFieldChange(row.id, 'testPrice', e.target.value)}
                                            variant="outlined"
                                            disabled={status === 'view'}
                                        />
                                    </TableCell>
                                    <TableCell style={{ padding: '8px 16px' }}>{calculateLineTotal(row.id, row)}</TableCell>
                                    <TableCell style={{ padding: '8px 16px' }}>
                                        <TextField
                                            size="small"
                                            value={String(getFieldValue(row.id, 'reference', row.reference || '') || '')}
                                            onChange={(e) => handleFieldChange(row.id, 'reference', e.target.value)}
                                            variant="outlined"
                                            disabled={status === 'view'}
                                            sx={{ width: '100%' }}
                                        />
                                    </TableCell>
                                    <TableCell style={{ padding: '8px 16px' }}>
                                        {status === 'view' ? null :
                                            <>
                                                <Tooltip title="Save">
                                                    <SaveOutlinedIcon
                                                        style={{ cursor: 'pointer', marginLeft: 8 }}
                                                        color="secondary"
                                                        sx={{ fontSize: 24 }}
                                                        onClick={() => handleRowSubmit(row, index)}
                                                    />
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <DeleteOutlineOutlinedIcon
                                                        style={{ cursor: 'pointer' }}
                                                        color="error"
                                                        sx={{ fontSize: 24 }}
                                                        onClick={() => handleRowDelete(row, index)}
                                                    />
                                                </Tooltip>
                                            </>
                                        }
                                    </TableCell>
                                </TableRow>
                                {expandedRows.has(row.id) && row.linkedTests?.length > 0 ?
                                    row.linkedTests.map((user: any, index: any) => {
                                        return (
                                            <Fragment key={user.id}>
                                                <TableRow tabIndex={-1} key={user.index} style={{ backgroundColor: '#00388324' }}>
                                                    <TableCell style={{ padding: '8px 16px' }}>
                                                        {String(user?.code || '')}
                                                        {String(getFieldValue(user.id, 'code', user.code) || '') === 'Grouped' ? <b>*</b> : null}
                                                    </TableCell>
                                                    <TableCell style={{ padding: '8px 16px' }}>
                                                        {String(user?.testName || '')}
                                                    </TableCell>
                                                    <TableCell style={{ padding: '8px 16px' }}>{String(user?.matrixType || '')}</TableCell>
                                                    <TableCell style={{ padding: '8px 16px' }}>{String(user?.noOfSamples || '')}</TableCell>
                                                    <TableCell style={{ padding: '8px 16px' }}>{String(user?.testPrice || '')}</TableCell>
                                                    <TableCell style={{ padding: '8px 16px' }}>{calculateLineTotal(user.id, user)}</TableCell>
                                                    <TableCell style={{ padding: '8px 16px' }}>{String(user?.reference || '')}</TableCell>
                                                    <TableCell style={{ padding: '8px 16px' }}>
                                                        {(user.billingGroupID === 0 || user.billingGroupID === null) ? (
                                                            <Tooltip title="Delete">
                                                                <DeleteOutlineOutlinedIcon
                                                                    style={{ cursor: 'pointer' }}
                                                                    color="error"
                                                                    sx={{ fontSize: 24 }}
                                                                    onClick={() => handleRowDelete(user, index)}
                                                                />
                                                            </Tooltip>
                                                        ) : null}
                                                    </TableCell>
                                                </TableRow>
                                            </Fragment>
                                        );
                                    })
                                    : null}
                            </>
                        ))}
                        <TableRow style={{ backgroundColor: '#0000000a' }}>
                            <TableCell rowSpan={3} />
                            <TableCell colSpan={2} />
                            <TableCell colSpan={2}><b>Total Analysis Price</b></TableCell>
                            <TableCell><b>{calculateTotalSubtotal()}</b></TableCell>
                            <TableCell colSpan={3} />
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
            <Snackbar
                open={notificationMessage?.open}
                autoHideDuration={6000}
                onClose={() => setNotificationMessage({ ...notificationMessage, open: false })}
            >
                <Alert
                    onClose={() => setNotificationMessage({ ...notificationMessage, open: false })}
                    severity={notificationMessage?.severity || 'info'}
                    sx={{ width: '100%' }}
                >
                    {notificationMessage?.message}
                </Alert>
            </Snackbar>
        </>
    );
}
